import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import clsx from "clsx";
import JobIcon from "@material-ui/icons/CodeOutlined";
import AddIcon from "@material-ui/icons/Add";
import PlayIcon from "@material-ui/icons/PlayArrowOutlined";
import StopIcon from "@material-ui/icons/Stop";
import BackIcon from "@material-ui/icons/ArrowBack";
import HistoryIcon from "@material-ui/icons/History";
import FunctionsIcon from "@material-ui/icons/Functions";
import SaveIcon from "@material-ui/icons/SaveOutlined";
import AiIcon from "@material-ui/icons/EmojiObjectsOutlined";
import { toast } from "react-toastify";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import CodeEditor from "../../components/CodeEditor";

// SCRIPTS (STU-186): scripts JavaScript de integração/regra, chamados
// pelos FLUXOS (nó "Rodar script"). Escritos aqui (com a IA) ou
// publicados pela IA do usuário via MCP (deploy_job). A execução e o log de cada
// chamada ficam no fluxo que chamou (canvas); aqui fica o código, as versões e o
// botão de testar com uma entrada de exemplo.

// paleta local — mesmos valores das telas de lista (Coletores/Automações)
const T = {
  bg: "#F6F7F9", panel: "#FFFFFF", line: "#E6E9ED", lineSoft: "#EEF0F3", hover: "#FAFBFC",
  ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4", ok: "#1E9E62", err: "#C0453B",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};
const LVL_COLOR = { warn: "#E5B54A", error: "#F08A80", debug: "#6B7785" };

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: T.bg },
  wrap: { width: "100%", maxWidth: 1100, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  wrapWide: { maxWidth: 1320 },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
  headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF0F3", color: "#5B6470", marginTop: 2 },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: T.ink },
  subtitle: { color: T.ink3, fontSize: 13, marginTop: 4, maxWidth: 780, lineHeight: 1.5 },
  grow: { flexGrow: 1 },
  code: { fontFamily: T.mono, fontSize: 12, background: "#F1F3F5", borderRadius: 6, padding: "1px 6px" },
  flowLink: { color: T.ink, fontWeight: 600, cursor: "pointer", textDecoration: "underline", textDecorationColor: T.line, textUnderlineOffset: 3, "&:hover": { textDecorationColor: T.ink } },

  // lista
  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    display: "flex", alignItems: "center", gap: 14, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16,
    padding: "12px 16px", cursor: "pointer", transition: "box-shadow .15s ease", "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)" },
  },
  avatar: { width: 40, height: 40, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 } },
  main: { flex: 1, minWidth: 0 },
  infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 600, marginBottom: 4 },
  name: { fontWeight: 600, fontSize: 14, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  col: { width: 260, flexShrink: 0, minWidth: 0 },
  colVal: { fontSize: 12.5, color: T.ink2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  mono: { fontFamily: T.mono, fontSize: 12, color: T.ink3 },
  empty: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center", color: T.ink3, fontSize: 13.5, lineHeight: 1.6 },

  // botões
  btnDark: {
    textTransform: "none", fontSize: 13, fontWeight: 600, color: "#fff", background: theme.app.dark, borderRadius: 8, padding: "7px 14px", boxShadow: "none",
    "&:hover": { background: theme.app.ink, boxShadow: "none" }, "& svg": { fontSize: 18 },
    "&.Mui-disabled": { background: theme.app.border, color: theme.app.textTertiary },
  },
  btnLight: { textTransform: "none", fontSize: 13, fontWeight: 600, color: T.ink, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 14px", "& svg": { fontSize: 18 } },
  btnOn: { background: "#EFF8D6", borderColor: "#cfe3a3" },

  // editor
  back: { display: "inline-flex", alignItems: "center", gap: 4, color: T.ink3, fontSize: 13, cursor: "pointer", marginBottom: 10, "& svg": { fontSize: 16 }, "&:hover": { color: T.ink } },
  editorBar: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: theme.spacing(1, 0, 1.25) },
  editorTitle: { fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: T.ink2 },
  pill: { fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-block", background: "#FFF4DD", color: "#9a6a00" },
  editorRow: { display: "flex", gap: 14, alignItems: "stretch" },
  editorCol: { flex: 1, minWidth: 0 },
  editorBox: { border: `1px solid ${T.line}`, borderRadius: 12, overflow: "hidden", background: T.panel, "& .cm-editor": { fontSize: 13 }, "& .cm-scroller": { fontFamily: T.mono } },
  editorHint: { fontSize: 12, color: T.ink3, marginTop: 6 },
  aiBar: { display: "flex", alignItems: "center", gap: 8, marginTop: 8, color: T.ink2, fontSize: 13 },
  refPanel: { width: 300, flexShrink: 0, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 8px", maxHeight: "calc(64vh + 2px)", overflowY: "auto" },
  refSec: { marginBottom: 10 },
  refTitle: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 700, padding: "4px 8px" },
  refItem: { display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 8, cursor: "pointer", "&:hover": { background: T.lineSoft }, "&:hover $refAdd": { opacity: 1 } },
  refAccount: { "& $refLabel": { color: T.ink2 } },
  refLabel: { fontFamily: T.mono, fontSize: 12, color: T.ink, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  refAdd: { fontSize: 16, color: T.ink3, opacity: 0 },
  refFoot: { fontSize: 11.5, color: T.ink3, padding: "6px 8px 2px", lineHeight: 1.45 },

  // modais
  dlg: { padding: theme.spacing(2.5), display: "flex", flexDirection: "column", gap: 14, width: 520, maxWidth: "calc(100vw - 32px)" },
  dlgWide: { width: 760 },
  dlgTitle: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 17, color: T.ink },
  dlgHint: { fontSize: 12.5, color: T.ink3, lineHeight: 1.5, marginTop: -6 },
  dlgActions: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 },
  label: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 700 },
  area: { width: "100%", minHeight: 120, fontFamily: T.mono, fontSize: 12.5, border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px", resize: "vertical", outline: "none", color: T.ink, "&:focus": { borderColor: T.ink3 } },
  pre: { fontFamily: T.mono, fontSize: 12, lineHeight: 1.5, background: "#F6F7F9", border: `1px solid ${T.lineSoft}`, borderRadius: 10, padding: "10px 12px", maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, color: T.ink },
  logBox: { fontFamily: T.mono, fontSize: 12, lineHeight: 1.55, background: "#0F141A", color: "#D7DEE6", borderRadius: 10, padding: "10px 12px", maxHeight: 260, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" },
  logT: { color: "#6B7785", marginRight: 8 },
  resHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.ink2 },
  badge: { color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 9px" },
  errBox: { fontSize: 13, color: T.err, background: "#FCEDEC", borderRadius: 8, padding: "8px 12px", whiteSpace: "pre-wrap" },
  toggleRow: { display: "flex", alignItems: "center", gap: 2, fontSize: 13, color: T.ink2 },
  verRow: { display: "flex", alignItems: "center", gap: 12, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: "8px 8px 8px 14px" },
  verName: { fontSize: 13, color: T.ink, flex: 1, minWidth: 0 },
}));

const fmtVer = d => (d ? format(new Date(d), "dd/MM HH:mm") : "");
const stripFences = s => String(s || "").replace(/^\s*```[a-z]*\s*\n?/i, "").replace(/\n?```\s*$/, "");

// ── o que existe dentro do script (painel + autocompletar + contexto da IA) ──
const buildReference = (secrets, datasets) => [
  {
    title: "Entrada e contexto",
    items: [
      { label: "studio.entrada", snippet: "studio.entrada", desc: "Objeto que o fluxo passou no nó \"Rodar script\" (campo Entrada)." },
      { label: "studio.dryRun", snippet: "if (studio.dryRun) {\n  console.log(\"teste: não executei\");\n}", desc: "true no modo de teste: registre o que faria, sem executar ações reais." },
      { label: "studio.runId", snippet: "studio.runId", desc: "Id desta rodada (útil para rastrear no log e nas tabelas)." },
      { label: "return", snippet: "return { ok: true };", desc: "O que você devolver vira a saída \"resultado\" do nó no fluxo." },
    ],
  },
  {
    title: "Log",
    items: [
      { label: "console.log", snippet: "console.log(\"\");", desc: "Linha no log da rodada (aparece no canvas do fluxo)." },
      { label: "console.warn", snippet: "console.warn(\"\");", desc: "Linha de aviso." },
      { label: "console.error", snippet: "console.error(\"\");", desc: "Linha de erro. Para FALHAR a rodada, use throw new Error(\"...\")." },
    ],
  },
  {
    title: "Segredos",
    items: [
      { label: "studio.segredo", snippet: "await studio.segredo(\"NOME\")", desc: "Lê um segredo (cadastre em Construtor › Segredos). O valor nunca aparece no log." },
      ...secrets.map(x => ({ label: `studio.segredo("${x.name}")`, snippet: `await studio.segredo("${x.name}")`, desc: "Segredo cadastrado nesta conta.", account: true })),
    ],
  },
  {
    title: "E-mail",
    items: [
      { label: "studio.email", snippet: "await studio.email({\n  to: \"\",\n  subject: \"\",\n  body: \"\",\n});", desc: "Envia e-mail (body em texto ou HTML). Opcionais: replyTo, fromName, connectorId (SMTP próprio)." },
    ],
  },
  {
    title: "Tabelas",
    items: [
      { label: "studio.tabela(...).buscar", snippet: "await studio.tabela(\"Nome\").buscar([{ campo: \"\", op: \"=\", valor: \"\" }], 100)", desc: "Lista registros. Operadores: = != > < >= <= contém vazio preenchido." },
      { label: "studio.tabela(...).inserir", snippet: "await studio.tabela(\"Nome\").inserir({ })", desc: "Grava um registro (ou uma lista)." },
      ...datasets.map(d => ({
        label: `studio.tabela("${d.name}")`,
        snippet: `await studio.tabela("${d.name}").buscar([], 100)`,
        desc: `Campos: ${(d.fields || []).map(f => f.name).join(", ") || "—"}`,
        account: true,
      })),
    ],
  },
  {
    title: "Bibliotecas",
    items: [
      { label: "require(\"axios\")", snippet: "const http = require(\"axios\");", desc: "Requisições HTTP (cada uma até 30 s)." },
      { label: "require(\"dayjs\")", snippet: "const dayjs = require(\"dayjs\");", desc: "Datas." },
      { label: "require(\"lodash\")", snippet: "const _ = require(\"lodash\");", desc: "Utilitários de listas e objetos." },
      { label: "require(\"crypto\")", snippet: "const crypto = require(\"crypto\");", desc: "Hash, assinaturas." },
    ],
  },
];

// resumo dos recursos da conta para a IA escrever com os nomes certos
const aiContext = (secrets, datasets) =>
  [
    `Segredos cadastrados: ${secrets.map(s => s.name).join(", ") || "(nenhum)"}`,
    `Tabelas: ${datasets.map(d => `"${d.name}" (${(d.fields || []).map(f => f.name).join(", ")})`).join("; ") || "(nenhuma)"}`,
  ].join("\n");

// escreve código com IA (stream): cada pedaço acumulado chega em onDelta
const streamAiCode = async ({ instructions, currentCode, context, signal, onDelta }) => {
  const resp = await fetch("/api/ai/code/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: (api.defaults.headers || {}).Authorization || "" },
    body: JSON.stringify({ instructions, currentCode, mode: "job", context }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    let msg = `Falha (${resp.status})`;
    try {
      const j = await resp.json();
      if (j && j.error) msg = j.error;
    } catch (e) {
      /* mantém o status */
    }
    throw new Error(msg);
  }
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let acc = "";
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const line = buf.slice(0, i).split("\n").find(l => l.startsWith("data:"));
      buf = buf.slice(i + 2);
      if (!line) continue;
      let msg;
      try {
        msg = JSON.parse(line.slice(5).trim());
      } catch (e) {
        continue;
      }
      if (msg.delta) {
        acc += msg.delta;
        onDelta(acc);
      } else if (msg.error) throw new Error(msg.error);
    }
  }
  return stripFences(acc);
};

// recursos da conta usados pelo editor (painel, autocompletar, IA)
const useAccountResources = () => {
  const [secrets, setSecrets] = useState([]);
  const [datasets, setDatasets] = useState([]);
  useEffect(() => {
    api.get("/secrets").then(({ data }) => setSecrets(data.secrets || [])).catch(() => {});
    api.get("/datasets").then(({ data }) => setDatasets(data.datasets || [])).catch(() => {});
  }, []);
  return { secrets, datasets };
};

// ── modal "Testar": entrada de exemplo + resultado e log da rodada ──────────
const TestDialog = ({ open, onClose, onRun, classes, paperClass }) => {
  const [input, setInput] = useState("{}");
  const [dry, setDry] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState(null);
  const run = async () => {
    setErr("");
    setRes(null);
    let parsed;
    try {
      parsed = input.trim() ? JSON.parse(input) : {};
    } catch (e) {
      setErr("A entrada não é um JSON válido.");
      return;
    }
    setBusy(true);
    try {
      setRes(await onRun(parsed, dry));
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally {
      setBusy(false);
    }
  };
  const ok = res && res.status === "success";
  const logs = (res && res.logs) || [];
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="md" PaperProps={{ className: paperClass }}>
      <div className={clsx(classes.dlg, classes.dlgWide)}>
        <div className={classes.dlgTitle}>Testar script</div>
        <div className={classes.dlgHint}>
          Roda o código salvo com esta entrada no lugar do que o fluxo passaria (<span className={classes.code}>studio.entrada</span>).
          No modo de teste o script recebe <span className={classes.code}>studio.dryRun = true</span> e não deve executar ações reais.
        </div>
        <div className={classes.label}>Entrada (JSON)</div>
        <textarea className={classes.area} value={input} onChange={e => setInput(e.target.value)} spellCheck={false} />
        <span className={classes.toggleRow}>
          <Switch size="small" color="primary" checked={dry} onChange={e => setDry(e.target.checked)} />
          Modo de teste {dry ? "" : "— DESLIGADO: o script executa as ações de verdade"}
        </span>
        {err ? <div className={classes.errBox}>{err}</div> : null}
        {res && (
          <>
            <div className={classes.resHead}>
              <span className={classes.badge} style={{ background: ok ? T.ok : T.err }}>{ok ? "sucesso" : res.status === "skipped" ? "pulada" : res.running ? "rodando" : "erro"}</span>
              {res.durationMs != null ? <span>{(res.durationMs / 1000).toFixed(1).replace(".", ",")} s</span> : null}
              {res.running ? <span>A rodada passou de 50 s e continua em execução.</span> : null}
            </div>
            {res.error ? <div className={classes.errBox}>{res.error}</div> : null}
            {res.result != null ? (
              <>
                <div className={classes.label}>Resultado (return)</div>
                <pre className={classes.pre}>{JSON.stringify(res.result, null, 2)}</pre>
              </>
            ) : null}
            <div className={classes.label}>Log ({logs.length})</div>
            <div className={classes.logBox}>
              {logs.length === 0 ? <span style={{ color: "#6B7785" }}>(sem console.log nesta rodada)</span> : null}
              {logs.map((l, i) => (
                <div key={i} style={{ color: LVL_COLOR[l.lvl] }}>
                  <span className={classes.logT}>{l.t ? format(new Date(l.t), "HH:mm:ss") : ""}</span>
                  {l.msg}
                </div>
              ))}
            </div>
          </>
        )}
        <div className={classes.dlgActions}>
          <Button className={classes.btnLight} disabled={busy} onClick={onClose}>Fechar</Button>
          <Button className={classes.btnDark} disabled={busy} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PlayIcon />} onClick={run}>
            {busy ? "Rodando…" : dry ? "Rodar teste" : "Rodar de verdade"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

// ── editor de um script ────────────────────────────────────────────────
const JobDetail = ({ jobId }) => {
  const classes = useStyles();
  const m = useMenuStyles();
  const history = useHistory();
  const location = useLocation();
  const { confirm } = useConfirm();
  const editorRef = useRef(null);
  const aiAbortRef = useRef(null);
  const { secrets, datasets } = useAccountResources();
  const [job, setJob] = useState(null);
  const [code, setCode] = useState(null); // rascunho do editor (null = carregando)
  const [saving, setSaving] = useState(false);
  const [refOpen, setRefOpen] = useState(true);
  const [histOpen, setHistOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`, { params: { limit: 1 } });
      setJob(data);
      setCode(c => (c == null ? data.code || "" : c));
    } catch (err) {
      toastError(err);
    }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const reference = useMemo(() => buildReference(secrets, datasets), [secrets, datasets]);
  const completions = useMemo(
    () => reference.flatMap(sec => sec.items.map(it => ({ label: it.label, apply: it.snippet, detail: sec.title, info: it.desc, type: "function" }))),
    [reference]
  );

  const dirty = job && code != null && code !== (job.code || "");
  const save = async () => {
    if (!dirty || saving) return true;
    setSaving(true);
    try {
      await api.put(`/jobs/${jobId}/code`, { code });
      const { data } = await api.get(`/jobs/${jobId}`, { params: { limit: 1 } });
      setJob(data);
      toast.success("Código salvo (a versão anterior ficou em Versões).");
      return true;
    } catch (err) {
      toastError(err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Testar: salva o que está na tela e roda com a entrada do modal
  const runTest = async (input, dryRun) => {
    if (!dryRun && !(await confirm({ title: "Rodar de verdade?", message: "O script vai rodar FORA do modo de teste — ele executa as ações reais (ex.: bloquear, enviar).", confirmText: "Rodar de verdade" }))) return null;
    if (dirty && !(await save())) return null;
    const { data } = await api.post(`/jobs/${jobId}/run`, { dryRun, input }, { timeout: 330000 });
    return data;
  };

  const writeWithAi = async instructions => {
    const text = String(instructions || "").trim();
    if (!text || aiBusy) return;
    setAiOpen(false);
    setAiBusy(true);
    const controller = new AbortController();
    aiAbortRef.current = controller;
    const prev = code || "";
    try {
      const out = await streamAiCode({
        instructions: text,
        currentCode: prev,
        context: aiContext(secrets, datasets),
        signal: controller.signal,
        onDelta: acc => setCode(acc),
      });
      setCode(out);
      setAiText("");
      toast.info("Código escrito pela IA — revise e clique em Salvar.");
    } catch (e) {
      if (e.name !== "AbortError") {
        setCode(prev);
        toast.error(e.message || "Falha ao escrever com IA.");
      }
    } finally {
      setAiBusy(false);
      aiAbortRef.current = null;
    }
  };
  // vindo de "Novo script" com descrição: a IA escreve assim que abrir
  const autoAiRef = useRef(false);
  useEffect(() => {
    const prompt = location.state && location.state.aiPrompt;
    if (!prompt || autoAiRef.current || code == null) return;
    autoAiRef.current = true;
    history.replace({ pathname: location.pathname, state: {} });
    writeWithAi(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, location.state]);

  const restore = async v => {
    if (!(await confirm({ title: "Voltar para esta versão?", message: `O editor recebe o código de ${fmtVer(v.at)}${v.by ? ` (${v.by})` : ""}. Ele só vale depois de Salvar — e o código atual vira uma versão também.`, confirmText: "Usar esta versão" }))) return;
    setCode(v.code || "");
    setHistOpen(false);
  };

  if (!job || code == null) return <div className={classes.page}><div className={classes.wrap} /></div>;
  const versions = job.history || [];
  return (
    <div className={classes.page}>
      <div className={clsx(classes.wrap, classes.wrapWide)}>
        <div className={classes.back} onClick={() => history.push("/jobs")}><BackIcon /> Scripts</div>
        <div className={classes.header}>
          <span className={classes.headIcon}><JobIcon /></span>
          <div className={classes.grow}>
            <div className={classes.title}>{job.name}</div>
            <div className={classes.subtitle}>
              {(job.usedBy || []).length
                ? <>Chamada pelo{(job.usedBy || []).length > 1 ? "s fluxos" : " fluxo"} {(job.usedBy || []).map((f, i) => (
                    <React.Fragment key={f.id}>{i ? ", " : ""}<span className={classes.flowLink} onClick={() => history.push(`/applications/${f.id}`)}>{f.name}</span>{f.active ? "" : " (desativado)"}</React.Fragment>
                  ))}. A execução e o log de cada chamada ficam no fluxo.</>
                : <>Nenhum fluxo chama este script ainda. Num fluxo, adicione o nó <b>Rodar script</b> — no horário (gatilho Agendado) ou num evento.</>}
            </div>
          </div>
        </div>

        <div className={classes.editorBar}>
          <span className={classes.editorTitle}>Código</span>
          {dirty ? <span className={classes.pill}>não salvo</span> : <span className={classes.mono}>salvo</span>}
          <span className={classes.grow} />
          <Button className={classes.btnLight} startIcon={<AiIcon />} disabled={aiBusy} onClick={() => setAiOpen(true)}>Escrever com IA</Button>
          <Button className={classes.btnLight} startIcon={<HistoryIcon />} disabled={!versions.length} onClick={() => setHistOpen(true)}>Versões ({versions.length})</Button>
          <Button className={clsx(classes.btnLight, refOpen && classes.btnOn)} startIcon={<FunctionsIcon />} onClick={() => setRefOpen(o => !o)}>Variáveis e funções</Button>
          <Button className={classes.btnLight} startIcon={<PlayIcon />} disabled={aiBusy} onClick={() => setTestOpen(true)}>Testar</Button>
          <Button className={classes.btnDark} startIcon={<SaveIcon />} disabled={!dirty || saving || aiBusy} onClick={save}>{saving ? "Salvando…" : "Salvar"}</Button>
        </div>
        <div className={classes.editorRow}>
          <div className={classes.editorCol}>
            <div className={classes.editorBox}>
              <CodeEditor ref={editorRef} value={code} onChange={setCode} readOnly={aiBusy} height="64vh" completions={completions} onSave={save} />
            </div>
            {aiBusy ? (
              <div className={classes.aiBar}>
                <CircularProgress size={16} />
                <span className={classes.grow}>A IA está escrevendo o código…</span>
                <Button className={classes.btnLight} startIcon={<StopIcon />} onClick={() => aiAbortRef.current && aiAbortRef.current.abort()}>Parar</Button>
              </div>
            ) : (
              <div className={classes.editorHint}>
                Corpo de uma função async: use <span className={classes.code}>await</span> e <span className={classes.code}>return</span>. Digite <span className={classes.code}>studio.</span> para ver as funções · Ctrl+S salva.
              </div>
            )}
          </div>
          {refOpen && (
            <div className={classes.refPanel}>
              {reference.map(sec => (
                <div key={sec.title} className={classes.refSec}>
                  <div className={classes.refTitle}>{sec.title}</div>
                  {sec.items.map(it => (
                    <Tooltip key={it.label} title={it.desc} placement="left">
                      <div className={clsx(classes.refItem, it.account && classes.refAccount)} onClick={() => editorRef.current && editorRef.current.insertAtCursor(it.snippet)}>
                        <span className={classes.refLabel}>{it.label}</span>
                        <AddIcon className={classes.refAdd} />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              ))}
              <div className={classes.refFoot}>Clique para inserir no cursor. Limites: 5 min por rodada, 30 s por requisição HTTP.</div>
            </div>
          )}
        </div>
      </div>

      <TestDialog open={testOpen} onClose={() => setTestOpen(false)} onRun={runTest} classes={classes} paperClass={m.dialogPaper} />

      <Dialog open={aiOpen} onClose={() => setAiOpen(false)} PaperProps={{ className: m.dialogPaper }}>
        <div className={classes.dlg}>
          <div className={classes.dlgTitle}>Escrever com IA</div>
          <div className={classes.dlgHint}>
            Descreva o que o script deve fazer (ou o que mudar no código atual). A IA já conhece o <span className={classes.code}>studio.*</span>, os seus segredos e as suas tabelas.
          </div>
          <TextField variant="outlined" multiline rows={5} autoFocus value={aiText} onChange={e => setAiText(e.target.value)}
            placeholder="Ex.: consultar a API X com o token do segredo X_TOKEN, filtrar os pedidos de hoje e devolver o total por status." />
          <div className={classes.dlgActions}>
            <Button className={classes.btnLight} onClick={() => setAiOpen(false)}>Cancelar</Button>
            <Button className={classes.btnDark} startIcon={<AiIcon />} disabled={!aiText.trim()} onClick={() => writeWithAi(aiText)}>Escrever</Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="md" PaperProps={{ className: m.dialogPaper }}>
        <div className={clsx(classes.dlg, classes.dlgWide)}>
          <div className={classes.dlgTitle}>Versões anteriores</div>
          <div className={classes.dlgHint}>Cada vez que o código é salvo (aqui ou pela IA via MCP), a versão anterior fica guardada. Últimas 30.</div>
          <div className={classes.list}>
            {versions.map(v => (
              <div key={v.idx} className={classes.verRow}>
                <HistoryIcon style={{ fontSize: 16, color: T.ink3 }} />
                <span className={classes.verName}>{fmtVer(v.at)} · {v.by || "—"}</span>
                <span className={classes.mono}>{v.chars} caracteres</span>
                <Button className={classes.btnLight} onClick={() => restore(v)}>Usar esta versão</Button>
              </div>
            ))}
          </div>
          <div className={classes.dlgActions}>
            <Button className={classes.btnLight} onClick={() => setHistOpen(false)}>Fechar</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// ── lista ────────────────────────────────────────────────────────────────
const JobList = () => {
  const classes = useStyles();
  const m = useMenuStyles();
  const history = useHistory();
  const { activeProjectId } = useProject();
  const [jobs, setJobs] = useState(null);
  const [create, setCreate] = useState(null); // { name, prompt }
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs", { params: activeProjectId ? { projectId: activeProjectId } : {} });
      setJobs(data.jobs || []);
    } catch (err) {
      toastError(err);
      setJobs([]);
    }
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  // withAi: a IA escreve a partir da descrição; senão abre o editor com o modelo
  const doCreate = async withAi => {
    setCreating(true);
    try {
      const { data } = await api.post("/jobs", { name: create.name.trim(), projectId: activeProjectId });
      const prompt = withAi ? String(create.prompt || "").trim() : "";
      setCreate(null);
      history.push({ pathname: `/jobs/${data.jobId}`, state: prompt ? { aiPrompt: prompt } : {} });
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <span className={classes.headIcon}><JobIcon /></span>
          <div className={classes.grow}>
            <div className={classes.title}>Scripts</div>
            <div className={classes.subtitle}>
              Código JavaScript para integrações e regras que não cabem em poucos nós: consultar APIs, cruzar dados, aplicar regras.
              Escreva aqui com a ajuda da IA, ou deixe a sua IA publicar pelo MCP. Os fluxos chamam o script pelo nó <b>Rodar script</b>,
              no horário ou a cada evento.
            </div>
          </div>
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={() => setCreate({ name: "", prompt: "" })}>
            Novo script
          </Button>
        </div>

        {jobs && jobs.length === 0 ? (
          <div className={classes.empty}>
            Nenhum script ainda.<br />
            Crie o primeiro e use o nó <b>Rodar script</b> numa automação para chamá-lo.
          </div>
        ) : (
          <div className={classes.list}>
            {(jobs || []).map(j => (
              <div key={j.jobId} className={classes.card} onClick={() => history.push(`/jobs/${j.jobId}`)}>
                <span className={classes.avatar}><JobIcon /></span>
                <div className={classes.main}>
                  <div className={classes.infoLabel}>Nome</div>
                  <div className={classes.name}>{j.name}</div>
                </div>
                <div className={classes.col}>
                  <div className={classes.infoLabel}>Chamada por</div>
                  <div className={classes.colVal}>{(j.usedBy || []).length ? (j.usedBy || []).map(f => f.name).join(", ") : "nenhum fluxo ainda"}</div>
                </div>
                <div className={classes.col} style={{ width: 150 }}>
                  <div className={classes.infoLabel}>Tamanho</div>
                  <span className={classes.mono}>{j.codeChars} caracteres</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!create} maxWidth="md" onClose={() => !creating && setCreate(null)} PaperProps={{ className: m.dialogPaper }}>
        {create && (
          <div className={classes.dlg} style={{ width: 640, boxSizing: "border-box" }}>
            <div className={classes.dlgTitle}>Novo script</div>
            <TextField variant="outlined" size="small" label="Nome" autoFocus value={create.name} onChange={e => setCreate({ ...create, name: e.target.value })}
              placeholder="Ex.: Bloqueio de linhas por IMEI" />
            <TextField variant="outlined" multiline rows={4} label="O que o script deve fazer?" value={create.prompt}
              onChange={e => setCreate({ ...create, prompt: e.target.value })}
              helperText="Descreva e a IA escreve o código assim que o script abrir. Ou escreva você mesmo no editor." />
            <div className={classes.dlgActions}>
              <Button className={classes.btnLight} disabled={creating} onClick={() => setCreate(null)}>Cancelar</Button>
              <div className={classes.grow} />
              <Button className={classes.btnLight} disabled={!create.name.trim() || creating} onClick={() => doCreate(false)} startIcon={<JobIcon />} style={{ whiteSpace: "nowrap" }}>
                Escrever código manualmente
              </Button>
              <Button variant="contained" color="primary" disableElevation disabled={!create.name.trim() || !create.prompt.trim() || creating}
                onClick={() => doCreate(true)} startIcon={<AiIcon />} style={{ whiteSpace: "nowrap" }}>
                Escrever com IA
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

const Jobs = () => {
  const { jobId } = useParams();
  return jobId ? <JobDetail jobId={jobId} /> : <JobList />;
};

export default Jobs;
